import {useRef, useState} from 'react';
import {detailScale, Point, Size, zoomAround, zoomAt, ZoomPan} from '../../domain/zoomPan';
import {classifySwipe, isDoubleTap, isTap, SwipeAction, SwipeAxis, swipeAxis, Tap} from '../../domain/swipe';
import {ZoomPanControl} from './useZoomPan';

/** How far a swipe towards something unavailable (no next photo, no archive) still follows the finger. */
const RESISTANCE = 0.25;

type Gesture =
    | { kind: 'pan'; origin: Point; start: ZoomPan }
    | { kind: 'pinch'; startMid: Point; startDistance: number; start: ZoomPan }
    | { kind: 'swipe'; origin: Point; startTime: number; axis: SwipeAxis | null };

interface Press {
    origin: Point;
    time: number;
    /** How far the pointer strayed from where it went down. */
    distance: number;
    /** Another finger joined: a pinch, never a tap. */
    multi: boolean;
    touch: boolean;
}

/** A swipe in progress: how far the picture follows the finger along the axis. */
export interface SwipeDrag {
    axis: SwipeAxis;
    offset: number;
}

interface StageGesturesInput {
    zoom: ZoomPanControl;
    image: Size | null;
    /** The swipes that do something here; others only budge the picture. */
    swipes: Partial<Record<SwipeAction, () => void>>;
}

const midpoint = (a: Point, b: Point): Point => ({x: (a.x + b.x) / 2, y: (a.y + b.y) / 2});
const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Pointer gestures on the viewer's stage: double tap or double click zooms in and back out, two
 * fingers pinch-zoom, a zoomed picture pans under one finger or the mouse, and at fit a finger swipes
 * (`swipes`). Spread `handlers` on the stage, which needs `touch-action: none`.
 */
export function useStageGestures({zoom, image, swipes}: StageGesturesInput) {
    const pointers = useRef(new Map<number, Point>());
    const gesture = useRef<Gesture | null>(null);
    const press = useRef<Press | null>(null);
    const lastTap = useRef<Tap | null>(null);
    const [drag, setDrag] = useState<SwipeDrag | null>(null);
    // A pan or pinch is under way: the picture follows without a transition.
    const [active, setActive] = useState(false);

    /** (Re)starts the gesture the pointers down now make, from the current zoom. */
    function begin(time: number) {
        const points = [...pointers.current.values()];
        const view = zoom.latest();
        if (points.length >= 2) {
            gesture.current = {
                kind: 'pinch',
                startMid: midpoint(points[0], points[1]),
                startDistance: distance(points[0], points[1]) || 1,
                start: view,
            };
            setDrag(null);
        } else if (points.length === 1 && view.scale > 1) {
            gesture.current = {kind: 'pan', origin: points[0], start: view};
        } else if (points.length === 1 && press.current?.touch && !press.current.multi) {
            // Not the finger left over from a pinch: it would step, archive or close by accident.
            gesture.current = {kind: 'swipe', origin: points[0], startTime: time, axis: null};
        } else {
            gesture.current = null;
        }
    }

    function resisted(action: SwipeAction, offset: number): number {
        return swipes[action] ? offset : offset * RESISTANCE;
    }

    function onPointerDown(e: React.PointerEvent) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        // The stage's own buttons (arrows, zoom chip) are clicked, not dragged.
        if (e.target instanceof Element && e.target.closest('button')) return;
        try {
            // Keeps the moves coming when the pointer leaves the stage.
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
            // Not an active pointer (e.g. a synthetic event): the gesture works without capture.
        }
        const point = zoom.toStagePoint(e.clientX, e.clientY);
        pointers.current.set(e.pointerId, point);
        if (pointers.current.size === 1) {
            press.current = {origin: point, time: e.timeStamp, distance: 0, multi: false, touch: e.pointerType !== 'mouse'};
        } else if (press.current) {
            press.current.multi = true;
        }
        begin(e.timeStamp);
        setActive(true);
    }

    function onPointerMove(e: React.PointerEvent) {
        if (!pointers.current.has(e.pointerId)) return;
        const point = zoom.toStagePoint(e.clientX, e.clientY);
        pointers.current.set(e.pointerId, point);
        if (press.current) {
            press.current.distance = Math.max(press.current.distance, distance(point, press.current.origin));
        }
        const g = gesture.current;
        if (g?.kind === 'pan') {
            zoom.set({scale: g.start.scale, x: g.start.x + point.x - g.origin.x, y: g.start.y + point.y - g.origin.y});
        } else if (g?.kind === 'pinch') {
            const [a, b] = [...pointers.current.values()];
            zoom.set(zoomAround(g.start, g.start.scale * distance(a, b) / g.startDistance, g.startMid, midpoint(a, b), zoom.bounds));
        } else if (g?.kind === 'swipe') {
            const dx = point.x - g.origin.x;
            const dy = point.y - g.origin.y;
            g.axis ??= swipeAxis(dx, dy);
            if (g.axis === 'x') setDrag({axis: 'x', offset: resisted(dx < 0 ? 'next' : 'previous', dx)});
            else if (g.axis === 'y') setDrag({axis: 'y', offset: resisted(dy < 0 ? 'up' : 'down', dy)});
        }
    }

    function onPointerEnd(e: React.PointerEvent) {
        if (!pointers.current.has(e.pointerId)) return;
        const point = zoom.toStagePoint(e.clientX, e.clientY);
        pointers.current.delete(e.pointerId);
        const completed = e.type === 'pointerup';

        const g = gesture.current;
        if (g?.kind === 'swipe') {
            // Springs back (or the next photo replaces it).
            setDrag(null);
            const action = completed && g.axis
                ? classifySwipe(g.axis, point.x - g.origin.x, point.y - g.origin.y, e.timeStamp - g.startTime, zoom.bounds.stage)
                : null;
            if (action) swipes[action]?.();
        }
        if (g?.kind === 'pinch') zoom.settle();
        if (pointers.current.size > 0) {
            // A pinch that lost a finger carries on as a pan.
            begin(e.timeStamp);
            return;
        }
        gesture.current = null;
        setActive(false);

        const p = press.current;
        press.current = null;
        if (!completed || !p || p.multi || !isTap(p.distance, e.timeStamp - p.time)) return;
        const tap = {point: p.origin, time: e.timeStamp};
        if (!isDoubleTap(lastTap.current, tap)) {
            lastTap.current = tap;
            return;
        }
        lastTap.current = null;
        const view = zoom.latest();
        if (view.scale > 1) zoom.reset();
        else zoom.set(zoomAt(view, detailScale(image, zoom.bounds, p.touch), tap.point, zoom.bounds));
    }

    return {
        drag,
        /** A pan, pinch or swipe is under way. */
        active,
        handlers: {
            onPointerDown,
            onPointerMove,
            onPointerUp: onPointerEnd,
            onPointerCancel: onPointerEnd,
        },
    };
}
